(() => {
  if (customElements.get('cart-discount-form')) return;

  class CartDiscountForm extends HTMLElement {
    constructor() {
      super();
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleRemove = this.handleRemove.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="discount"]');
      this.submitButton = this.querySelector('button[type="submit"]');
      this.message = this.querySelector('[data-discount-message]');
      this.appliedDiscount = this.querySelector('[data-applied-discount]');
      this.appliedCode = this.querySelector('[data-applied-discount-code]');
      this.appliedPercentage = this.querySelector('[data-applied-discount-percentage]');
      this.removeButton = this.querySelector('[data-remove-discount]');

      this.form?.addEventListener('submit', this.handleSubmit);
      this.removeButton?.addEventListener('click', this.handleRemove);
      this.showAppliedCode(this.dataset.appliedCode || '');
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.removeButton?.removeEventListener('click', this.handleRemove);
    }

    async handleSubmit(event) {
      event.preventDefault();

      const code = this.input?.value.trim();
      if (!code) {
        this.showMessage(this.dataset.emptyMessage || 'Enter a discount code.', 'error');
        this.input?.focus();
        return;
      }

      this.setLoading(true);
      this.showMessage('', '');

      try {
        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('Unable to read current cart discount codes.');
        const currentCart = await cartResponse.json();
        const currentCodes = (currentCart.discount_codes || []).map((entry) => entry.code).filter(Boolean);
        const response = await fetch(this.dataset.updateUrl || routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            discount: [...new Set([...currentCodes, code])].join(','),
            sections: this.getSectionsToRender(),
            sections_url: window.location.pathname,
          }),
        });
        let state = await response.json();

        const hasErrors =
          state.errors && (typeof state.errors !== 'object' || Object.keys(state.errors).length > 0);
        if (!response.ok || hasErrors || state.status >= 400) {
          throw new Error(state.description || state.message || 'The discount code could not be applied.');
        }

        const validCode = this.hasAppliedCode(state, code);
        if (window.GymSetsMatVolume) state = await window.GymSetsMatVolume.sync(this.getSectionsToRender(), state);
        if (!validCode) {
          const updatedForm = this.renderCartSections(state, this.dataset.appliedCode || '');
          updatedForm?.showMessage(
            this.dataset.invalidMessage || 'That discount code is not valid for this cart.',
            'error'
          );
          return;
        }

        const updatedForm = this.renderCartSections(state, code);
        updatedForm?.showMessage('', '');
      } catch (error) {
        console.error('Cart discount update failed', error);
        const currentForm =
          document.querySelector(`cart-discount-form[data-cart-surface="${this.dataset.cartSurface || ''}"]`) || this;
        currentForm.showMessage(
          this.dataset.errorMessage || 'We could not apply that discount code. Please check the code and try again.',
          'error'
        );
      } finally {
        this.setLoading(false);
      }
    }

    async handleRemove(event) {
      event.preventDefault();

      this.setLoading(true);
      this.showMessage('', '');

      try {
        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('Unable to read current cart discount codes.');
        const currentCart = await cartResponse.json();
        const currentCodes = (currentCart.discount_codes || []).map((entry) => entry.code).filter(Boolean);
        const response = await fetch(this.dataset.updateUrl || routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            discount: currentCodes.filter((code) => code.toUpperCase() !== this.appliedCode?.textContent.trim().toUpperCase()).join(','),
            sections: this.getSectionsToRender(),
            sections_url: window.location.pathname,
          }),
        });
        let state = await response.json();

        const hasErrors =
          state.errors && (typeof state.errors !== 'object' || Object.keys(state.errors).length > 0);
        if (!response.ok || hasErrors || state.status >= 400) {
          throw new Error(state.description || state.message || 'The discount code could not be removed.');
        }

        if (window.GymSetsMatVolume) state = await window.GymSetsMatVolume.sync(this.getSectionsToRender(), state);
        const updatedForm = this.renderCartSections(state, '');
        updatedForm?.showMessage('', '');
      } catch (error) {
        console.error('Cart discount removal failed', error);
        this.showMessage(
          this.dataset.removeErrorMessage || 'We could not remove that discount code. Please try again.',
          'error'
        );
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.toggleAttribute('aria-busy', isLoading);
      this.submitButton?.toggleAttribute('aria-disabled', isLoading);

      if (this.submitButton) this.submitButton.disabled = isLoading;
      if (this.removeButton) this.removeButton.disabled = isLoading;
    }

    showMessage(message, status) {
      if (!this.message) return;

      this.message.hidden = !message;
      this.message.textContent = message;
      this.message.dataset.status = status;
      this.input?.setAttribute('aria-invalid', status === 'error' ? 'true' : 'false');
    }

    showAppliedCode(code) {
      if (!this.appliedDiscount || !this.appliedCode) return;

      const normalizedCode = String(code || '').trim();
      this.appliedCode.textContent = normalizedCode;

      if (this.appliedPercentage) {
        const percentage = normalizedCode ? this.dataset.appliedDiscountPercentage || '' : '';
        this.appliedPercentage.textContent = percentage ? `-${percentage}%` : '';
        this.appliedPercentage.hidden = !percentage;
      }

      this.appliedDiscount.hidden = !normalizedCode;
      this.removeButton?.toggleAttribute('aria-disabled', !normalizedCode);
      if (this.removeButton) this.removeButton.disabled = !normalizedCode;
    }

    hasAppliedCode(state, code) {
      const normalizedCode = code.trim().toUpperCase();
      if (Array.isArray(state.discount_codes)) {
        return state.discount_codes.some((entry) => entry.code?.toUpperCase() === normalizedCode && entry.applicable === true);
      }
      return (state.cart_level_discount_applications || []).some((discount) => discount.title?.toUpperCase() === normalizedCode);
    }

    getSectionsToRender() {
      const sections = ['cart-icon-bubble'];
      const mainCartItems = document.getElementById('main-cart-items');
      const mainCartFooter = document.getElementById('main-cart-footer');

      if (document.getElementById('CartDrawer')) sections.unshift('cart-drawer');
      if (mainCartItems?.dataset.id) sections.unshift(mainCartItems.dataset.id);
      if (mainCartFooter?.dataset.id) sections.unshift(mainCartFooter.dataset.id);

      return sections;
    }

    renderCartSections(state, code) {
      const drawerRoot = document.getElementById('CartDrawer');
      const drawerInner = drawerRoot?.querySelector('.drawer__inner');
      const drawerSection = state.sections?.['cart-drawer'];
      const mainCartItems = document.getElementById('main-cart-items');
      const mainCartItemsSection = mainCartItems?.dataset.id
        ? state.sections?.[mainCartItems.dataset.id]
        : null;
      const mainCartFooter = document.getElementById('main-cart-footer');
      const mainCartSection = mainCartFooter?.dataset.id
        ? state.sections?.[mainCartFooter.dataset.id]
        : null;
      const cartIconSection = state.sections?.['cart-icon-bubble'];
      const detailsOpen = drawerRoot?.querySelector('#Details-CartDrawer')?.open;

      if (
        (drawerInner && !drawerSection) ||
        (mainCartItems && !mainCartItemsSection) ||
        (mainCartFooter && !mainCartSection)
      ) {
        throw new Error('The updated cart summary could not be rendered.');
      }

      if (drawerInner && drawerSection) {
        const parsedDrawer = new DOMParser().parseFromString(drawerSection, 'text/html');
        const updatedDrawerInner = parsedDrawer.querySelector('.drawer__inner');
        if (!updatedDrawerInner) throw new Error('The updated cart drawer could not be rendered.');

        drawerInner.innerHTML = updatedDrawerInner.innerHTML;

        const details = drawerRoot.querySelector('#Details-CartDrawer');
        if (detailsOpen) details?.setAttribute('open', '');

        const cartDrawer = document.querySelector('cart-drawer');
        cartDrawer?.setSummaryAccessibilityForAll?.();
      }

      if (mainCartItems && mainCartItemsSection) {
        const parsedMainCartItems = new DOMParser().parseFromString(mainCartItemsSection, 'text/html');
        const updatedMainCartItems = parsedMainCartItems.querySelector('#main-cart-items');

        if (!updatedMainCartItems) {
          throw new Error('The updated main cart items could not be rendered.');
        }

        mainCartItems.innerHTML = updatedMainCartItems.innerHTML;
      }

      if (mainCartFooter && mainCartSection) {
        const parsedMainCart = new DOMParser().parseFromString(mainCartSection, 'text/html');
        const updatedMainCartContents = parsedMainCart.querySelector('#main-cart-footer .js-contents');
        const currentMainCartContents = mainCartFooter.querySelector('.js-contents');

        if (!updatedMainCartContents || !currentMainCartContents) {
          throw new Error('The updated main cart summary could not be rendered.');
        }

        currentMainCartContents.innerHTML = updatedMainCartContents.innerHTML;
      }

      if (cartIconSection) {
        const cartIcon = document.getElementById('cart-icon-bubble');
        const parsedCartIcon = new DOMParser().parseFromString(cartIconSection, 'text/html');
        const updatedCartIcon = parsedCartIcon.querySelector('.shopify-section');
        if (cartIcon && updatedCartIcon) cartIcon.innerHTML = updatedCartIcon.innerHTML;
      }

      const updatedForm = this.dataset.cartSurface === 'main-cart'
        ? mainCartFooter?.querySelector('cart-discount-form')
        : drawerRoot?.querySelector('cart-discount-form');
      updatedForm?.showAppliedCode(code);
      if (updatedForm?.input) updatedForm.input.value = '';

      return updatedForm;
    }
  }

  if (!customElements.get('cart-discount-form')) {
    customElements.define('cart-discount-form', CartDiscountForm);
  }
})();
